/**
 * configuration-engine-adapter.js — Karen Fabrics Configuration Engine Adapter
 *
 * THE SOLE FILE that calls the configuration engine evaluator service.
 * All other theme files that need calculation results call this adapter's
 * interface only — never the engine service directly.
 *
 * Ownership:
 * - Configuration_rule data: Shopify (metaobject) — engine READS, never owns
 * - All calculation formulas: Karen Configuration Layer (Rule 3)
 * - Engine evaluator: replaceable service behind this adapter
 *
 * P4 Phase 1 / Targets 1–4 — MM normalisation, input validation, and live
 * engine transport. evaluate() validates raw inputs (Target 2), normalises
 * spatial inputs to mm (Target 1), builds the RA-3 request (Target 3), and POSTs
 * it to the deployed engine via the Shopify App Proxy (Target 4), mapping the
 * RM-1 response back through mapEngineResponse(). Invalid inputs are rejected
 * fail-closed BEFORE normalisation; ANY transport error (network, non-200,
 * invalid JSON, malformed body) also fails closed to the empty result shape —
 * no normalisation shortcut, no invented price (Constitution Rule 11). Shopify
 * signs App Proxy requests and the Worker verifies that signature; the theme
 * stores no signing secret and computes no HMAC. Price is not an evaluator
 * output either — `config_snapshot.price_basis` is derived by the order-write
 * layer from verified Shopify order lines (ADR-004 PR-1 / Schema Lock Amendment
 * #6).
 *
 * Unit normalisation contract (locked — Fabric Calculator Architecture §4.4):
 * All customer inputs are normalised to millimetres (mm) — the canonical unit —
 * before engine evaluation. The engine never receives raw customer units.
 *
 *   normalizeInput(value, unit) = Math.round(value * TO_MM[unit])   // integer mm
 *
 * | Customer unit | Factor   | Result |
 * | metre (m)     | × 1000   | mm     |
 * | centimetre    | × 10     | mm     |
 * | yard          | × 914.4  | mm     |
 * | inch          | × 25.4   | mm     |
 *
 * Source: Service Register §2 · Fabric Calculator Architecture §4.2–§4.4
 *         Theme Foundation Architecture §11 · Theme Implementation Plan §5.4
 * Owner: Storefront Engineering (Karen Configuration Layer)
 */

/**
 * Spatial unit → millimetre conversion factors (Fabric Calculator §4.4, locked).
 * `mm: 1` is the identity case (a value already in the canonical unit).
 */
const TO_MM = {
  m: 1000,
  cm: 10,
  yard: 914.4,
  inch: 25.4,
  mm: 1,
};

/** Hard transport ceiling (TT-8). Every call is aborted after five seconds. */
const ENGINE_TIMEOUT_MS = 5000;

/**
 * Endpoint state is injected out-of-band by <kf-configurator-entry> from the
 * governed theme setting (TT-1). It is deliberately not hardcoded and never
 * enters evaluate()'s locked three-argument interface.
 */
let engineEndpoint = null;

/**
 * Validate and normalise a governed evaluator endpoint.
 *
 * Accepted forms:
 * - a same-origin Shopify App Proxy path beginning `/apps/`; or
 * - an absolute HTTPS URL for a separately governed direct transport.
 *
 * Query strings, fragments, credentials, protocol-relative URLs, path
 * traversal, and insecure schemes are rejected. Returns null on any failure.
 *
 * @param {unknown} candidate
 * @returns {string|null}
 */
function normaliseEngineEndpoint(candidate) {
  if (typeof candidate !== 'string') return null;
  const endpoint = candidate.trim();
  if (endpoint === '' || /[\\\s]/.test(endpoint)) return null;

  if (endpoint.startsWith('/')) {
    if (!endpoint.startsWith('/apps/') || endpoint.startsWith('//')) return null;
    if (endpoint.includes('?') || endpoint.includes('#')) return null;
    let parsed;
    try {
      parsed = new URL(endpoint, 'https://theme.invalid');
    } catch (_error) {
      return null;
    }
    if (parsed.pathname !== endpoint || parsed.pathname.includes('/../')) return null;
    return endpoint;
  }

  let parsed;
  try {
    parsed = new URL(endpoint);
  } catch (_error) {
    return null;
  }
  if (
    parsed.protocol !== 'https:' ||
    parsed.username !== '' ||
    parsed.password !== '' ||
    parsed.search !== '' ||
    parsed.hash !== ''
  ) {
    return null;
  }
  return parsed.href;
}

/**
 * Convert a single numeric value expressed in a spatial `unit` to INTEGER
 * millimetres.
 *
 * Contract (Fabric Calculator §4.4): `Math.round(value * factor)`. The result
 * is integer mm with no floating-point fraction carried forward; sub-mm error
 * from non-binary-exact factors (e.g. 25.4) is absorbed by the round.
 * `Math.round` rounds half toward +∞ — unambiguous for the strictly positive
 * dimensions this engine handles.
 *
 * @param {number} value - value in `unit`
 * @param {string} unit  - a key of TO_MM (m | cm | yard | inch | mm)
 * @returns {number} integer millimetres
 */
function toMillimetres(value, unit) {
  return Math.round(value * TO_MM[unit]);
}

/**
 * Normalise the spatial numeric inputs of a configuration to integer mm.
 *
 * Behaviour is governed by two P3/P4 governance rulings:
 *
 * RA-1 (conversion unit) — the conversion factor is taken from the rule-level
 *   `configurationRule.unit_of_measure`, per the explicit §4.4 normalisation
 *   contract (which keys off `configurationRule.unit_of_measure`). A per-input
 *   `unit` declared in `input_schema` is treated as classification/validation
 *   metadata and must equal the rule unit; it is NOT used to pick the factor.
 *   (Higher-precedence §4.4 governs over the per-input `unit` in §3.1.)
 *
 * RA-2 (which fields) — only SPATIAL numeric fields are normalised: an
 *   `input_schema` entry is spatial iff `type === 'number'` AND it declares a
 *   `unit` that is a recognised spatial unit (a key of TO_MM). Non-spatial
 *   numbers (no unit, or a non-length unit) and `choice` fields are left
 *   untouched — they receive no `_mm` field.
 *
 * Preservation — the return value is the original inputs object plus additive
 *   `<name>_mm` fields. Raw inputs are never mutated or removed, so
 *   `config_snapshot.raw_inputs` reproducibility is preserved.
 *
 * Fail-closed — missing/invalid `inputs` or `rule` yields `{}`; a non-finite
 *   value for a spatial field is skipped (no `_mm` field emitted).
 *
 * @param {Object} inputs - raw customer inputs (keyed by input name)
 * @param {Object} rule   - the configuration_rule metaobject entry
 * @returns {Object} the raw inputs plus additive `<name>_mm` integer-mm fields
 */
function normaliseInputs(inputs, rule) {
  if (!inputs || typeof inputs !== 'object') return {};
  if (!rule || typeof rule !== 'object') return {};

  // RA-1: conversion unit is the rule-level unit_of_measure.
  const ruleUnit = rule.unit_of_measure;
  const factorUnit = Object.prototype.hasOwnProperty.call(TO_MM, ruleUnit) ? ruleUnit : 'm';

  // Additive, non-destructive: start from a shallow copy of the raw inputs.
  const result = { ...inputs };

  const schema = Array.isArray(rule.input_schema) ? rule.input_schema : [];
  schema.forEach((field) => {
    if (!field || field.type !== 'number') return; // RA-2: numbers only

    // RA-2: spatial iff the field declares a recognised spatial unit.
    const isSpatial = Object.prototype.hasOwnProperty.call(TO_MM, field.unit);
    if (!isSpatial) return;

    const value = inputs[field.name];
    if (typeof value !== 'number' || !Number.isFinite(value)) return; // fail-closed

    result[`${field.name}_mm`] = toMillimetres(value, factorUnit);
  });

  return result;
}

/**
 * Validate raw customer inputs against a configuration_rule's `input_schema`
 * and `constraints` (P4 Phase 1 / Target 2).
 *
 * Pure function — reads only; mutates nothing. Returns a structured result so
 * the caller (and the boundary component) can decide whether to proceed.
 *
 * Governance rulings (Target 2 Pre-Build Audit):
 *   VR-1 (unknown fields) — an input not declared in `input_schema` is REJECTED
 *     (strict allow-list); the engine operates only on the declared schema.
 *   VR-2 (inclusivity) — `min`/`max` bounds are INCLUSIVE (value ≥ min, ≤ max).
 *   VR-3 (conjunction) — where `constraints.min_<field>` / `max_<field>` restate
 *     a numeric field's bound, it is enforced IN ADDITION to `input_schema`
 *     (most-restrictive). Constraints with no matching input field (e.g.
 *     `min_qty`, `area`) are OUT of Target-2 scope — they bound engine output,
 *     validated in a later target.
 *
 * Fail-closed — a missing/malformed rule, missing `input_schema`, or non-object
 *   inputs return `{ valid: false }`. Non-finite numbers and out-of-range /
 *   invalid-option / unknown / missing-required values are all rejected.
 *
 * @param {Object} inputs - raw customer inputs (keyed by input name)
 * @param {Object} rule   - the configuration_rule metaobject entry
 * @returns {{ valid: boolean, errors: string[] }}
 */
function validateInputs(inputs, rule) {
  // Fail-closed: malformed rule / schema.
  if (!rule || typeof rule !== 'object') {
    return { valid: false, errors: ['rule is missing or not an object'] };
  }
  if (!Array.isArray(rule.input_schema)) {
    return { valid: false, errors: ['rule.input_schema is missing or not an array'] };
  }
  // Fail-closed: inputs must be an object.
  if (!inputs || typeof inputs !== 'object') {
    return { valid: false, errors: ['inputs are missing or not an object'] };
  }

  const errors = [];
  const declared = new Set();

  rule.input_schema.forEach((field) => {
    if (!field || typeof field.name !== 'string') {
      errors.push('input_schema contains a malformed field entry');
      return;
    }
    declared.add(field.name);

    const value = inputs[field.name];
    const present = value !== undefined && value !== null;

    if (!present) {
      if (field.required) errors.push(`${field.name}: required field is missing`);
      return; // optional + absent → OK
    }

    if (field.type === 'number') {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        errors.push(`${field.name}: expected a finite number`);
        return;
      }
      // VR-2: inclusive range from input_schema.
      if (typeof field.min === 'number' && value < field.min) {
        errors.push(`${field.name}: ${value} is below the minimum of ${field.min}`);
      }
      if (typeof field.max === 'number' && value > field.max) {
        errors.push(`${field.name}: ${value} is above the maximum of ${field.max}`);
      }
    } else if (field.type === 'choice') {
      const options = Array.isArray(field.options) ? field.options : [];
      if (!options.includes(value)) {
        errors.push(`${field.name}: ${JSON.stringify(value)} is not a valid option`);
      }
    } else {
      errors.push(`${field.name}: unsupported field type "${field.type}"`);
    }
  });

  // VR-1: reject unknown input fields (strict allow-list).
  Object.keys(inputs).forEach((key) => {
    if (!declared.has(key)) errors.push(`${key}: unknown field not declared in input_schema`);
  });

  // VR-3: constraints conjunction for input-field-named numeric bounds.
  const constraints =
    rule.constraints && typeof rule.constraints === 'object' ? rule.constraints : {};
  rule.input_schema.forEach((field) => {
    if (!field || field.type !== 'number' || typeof field.name !== 'string') return;
    const value = inputs[field.name];
    if (typeof value !== 'number' || !Number.isFinite(value)) return;
    const min = constraints[`min_${field.name}`];
    const max = constraints[`max_${field.name}`];
    if (typeof min === 'number' && value < min) {
      errors.push(`${field.name}: ${value} is below the constraint min_${field.name} of ${min}`);
    }
    if (typeof max === 'number' && value > max) {
      errors.push(`${field.name}: ${value} is above the constraint max_${field.name} of ${max}`);
    }
  });

  return { valid: errors.length === 0, errors };
}

/**
 * The locked empty result shape (Service Register §2 output contract). Returned
 * unchanged on both the fail-closed (invalid) and the not-yet-wired valid path.
 *
 * @returns {Object}
 */
function emptyResult() {
  return {
    quantities: [],
    consumption: {
      fabric_metres: 0,
      fabric_consumption_mm: 0,
      waste_pct_applied: 0,
    },
    lead_time_days: 0,
  };
}

/**
 * Build the request payload sent from the adapter to the engine service
 * (P4 Phase 1 / Target 3 — RA-3, amended).
 *
 * Pure & synchronous. Does NOT call the engine (that is Target 4). The `rule`
 * envelope is present now so no payload redesign is needed at T4.
 *
 * Shape (RA-3):
 *   {
 *     payload_version: 1,
 *     rule: { key, version, consumption_logic, pricing_components, lead_time_logic },
 *     market,
 *     inputs: { ...normalisedInputs }   // Target-1 union: raw values + additive _mm
 *   }
 *
 * Fail-closed — returns `null` when the rule is missing/malformed (no string
 * `key` / numeric `version`), `normalisedInputs` is not a plain object, or
 * `market` is not a string. Computes nothing; copies the rule logic blocks
 * through verbatim (engine owns all calculation — Rule 3).
 *
 * @param {Object} rule             - the configuration_rule metaobject entry
 * @param {Object} normalisedInputs - output of normaliseInputs() (raw + _mm)
 * @param {string} market           - active Shopify market handle
 * @returns {Object|null} the request payload, or null when fail-closed
 */
function buildEngineRequest(rule, normalisedInputs, market) {
  if (!rule || typeof rule !== 'object' || typeof rule.key !== 'string' || typeof rule.version !== 'number') {
    return null;
  }
  if (!normalisedInputs || typeof normalisedInputs !== 'object' || Array.isArray(normalisedInputs)) {
    return null;
  }
  if (typeof market !== 'string') {
    return null;
  }

  return {
    payload_version: 1,
    rule: {
      key: rule.key,
      version: rule.version,
      consumption_logic: rule.consumption_logic,
      pricing_components: rule.pricing_components,
      lead_time_logic: rule.lead_time_logic,
    },
    market,
    inputs: { ...normalisedInputs },
  };
}

/**
 * Map an engine-service response into the locked output contract
 * (P4 Phase 1 / Target 3 — RM-1/RM-2/RM-3).
 *
 * Pure & synchronous. Maps 1:1 into the Service Register §2 output shape; the
 * adapter computes NOTHING — every value (including `fabric_consumption_mm`)
 * comes from the engine response (RM-1, Rule 3).
 *
 * RM-2 (fail-closed) — any missing/ill-typed required field returns
 *   `emptyResult()`. No defaults, no derived values, no partial output (Rule 11).
 * RM-3 (unknown fields) — extra fields, at top level or within array items /
 *   consumption, are ignored: the output is constructed from known keys only.
 *   A legacy `price_basis` field, if present, is one such ignored field — it is
 *   never mapped or persisted. Price is not an evaluator output; it is derived by
 *   the order-write layer from verified Shopify order lines (ADR-004 PR-1 /
 *   Schema Lock Amendment #6). The mapped output keys are exactly
 *   `quantities`, `consumption`, and `lead_time_days`.
 *
 * @param {Object} response - the engine service response
 * @returns {Object} the locked output contract, or emptyResult() on any defect
 */
function mapEngineResponse(response) {
  if (!response || typeof response !== 'object' || Array.isArray(response)) return emptyResult();

  const { quantities, consumption, lead_time_days } = response;

  // RM-2: required top-level fields, correct shape.
  if (!Array.isArray(quantities)) return emptyResult();
  if (typeof lead_time_days !== 'number' || !Number.isFinite(lead_time_days)) return emptyResult();
  if (!consumption || typeof consumption !== 'object' || Array.isArray(consumption)) return emptyResult();

  // RM-1/RM-2: consumption sub-fields come from the engine; all three required.
  const { fabric_metres, fabric_consumption_mm, waste_pct_applied } = consumption;
  const finite = (n) => typeof n === 'number' && Number.isFinite(n);
  if (!finite(fabric_metres) || !finite(fabric_consumption_mm) || !finite(waste_pct_applied)) {
    return emptyResult();
  }

  // RM-3: map quantities to known keys only; fail-closed on any malformed item.
  const mappedQuantities = [];
  for (const q of quantities) {
    if (!q || typeof q !== 'object' || typeof q.unit_sku !== 'string' || !finite(q.quantity)) {
      return emptyResult();
    }
    mappedQuantities.push({ unit_sku: q.unit_sku, quantity: q.quantity });
  }

  return {
    quantities: mappedQuantities,
    consumption: { fabric_metres, fabric_consumption_mm, waste_pct_applied },
    lead_time_days,
  };
}

/**
 * Build the engine-owned fragment of the `config_snapshot` order metafield
 * (P4 Phase 1 / Target 5 — SM-1…SM-10, adapter-interfaces.md §3.10).
 *
 * Pure & synchronous. Performs NO network call, NO calculation, and NO
 * persistence. It maps an already-resolved engine response (the output of
 * `mapEngineResponse`) into the locked `config_snapshot` shape (Schema Lock §4.7),
 * emitting ONLY the fields the engine owns. The full snapshot is assembled and
 * written write-once at order creation by the server-side order-write layer
 * (Rule 12) — this function never writes the metafield.
 *
 * Authority (SM-1): Schema Lock §4.7 (Level 2) governs persisted field names; the
 *   engine response is mapped TO it. Emitting the locked keys is not a schema
 *   change (Schema Lock §7.3).
 *
 * Mapping rulings:
 *   SM-1  fabric_metres → computed_outputs.fabric_consumption (rename, metres).
 *   SM-2  consumption.waste_pct_applied is DROPPED (undeclared in §4.7;
 *         reconstructable from rule_key + rule_version).
 *   SM-3  price_basis is NOT emitted by this engine-owned fragment. Price is not
 *         an evaluator output; `config_snapshot.price_basis` is assembled by the
 *         order-write layer from verified Shopify order lines at order creation
 *         (ADR-004 PR-1 / Schema Lock Amendment #6). This fragment carries no
 *         unit_price, subtotal, or currency.
 *   SM-5  raw_inputs is the pristine customer inputs — any reserved `*_mm`
 *         normalisation key is excluded so the normalised union never leaks in.
 *   SM-6  computes nothing (Rule 3): rename/reposition/exclude only, no
 *         arithmetic; fabric_consumption is taken from fabric_metres and is never
 *         derived from fabric_consumption_mm.
 *   SM-7  fail-closed → null unless ALL hold: the required computed_outputs source
 *         fields are finite; rule.key/version/unit_of_measure are valid;
 *         rule.pricing_components yields a non-empty authoritative set of governed
 *         unit_sku handles; and every mappedResponse.quantities item names a
 *         governed handle with a positive safe-integer quantity
 *         (Number.isSafeInteger(q) && q > 0). Malformed items, unknown/non-governed
 *         handles, and zero/negative/fractional/non-finite/unsafe quantities all
 *         fail closed. quantities are the price-free "engine produced a valid
 *         result" signal — read, never persisted (SM-10). Validity does not depend
 *         on any evaluator price data. (Slice-5 exact-coverage / duplicate-key
 *         checks are out of scope for this fragment.)
 *   SM-8  engine fragment only: Kickflip references, fabric_spec fields,
 *         order/line/market/currency context, and bundle_ref are NOT emitted here.
 *   SM-9  production_spec mapping is out of scope (no field is produced here).
 *   SM-10 quantities[] are transient (cart only) and are NOT persisted.
 *
 * @param {Object} rule          - the configuration_rule metaobject entry
 * @param {Object} rawInputs     - pristine validated customer inputs (no `_mm` union)
 * @param {Object} mappedResponse - output of mapEngineResponse() (RM-1/2/3-clean)
 * @returns {Object|null} the config_snapshot engine fragment, or null fail-closed
 */
function buildConfigSnapshotFragment(rule, rawInputs, mappedResponse) {
  const finite = (n) => typeof n === 'number' && Number.isFinite(n);

  // SM-7: rule identity fields must be valid.
  if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return null;
  if (typeof rule.key !== 'string') return null;
  if (!finite(rule.version)) return null;
  if (typeof rule.unit_of_measure !== 'string') return null;

  // SM-5/SM-7: raw inputs must be a plain object.
  if (!rawInputs || typeof rawInputs !== 'object' || Array.isArray(rawInputs)) return null;

  // SM-7: mapped response must be a plain object.
  if (!mappedResponse || typeof mappedResponse !== 'object' || Array.isArray(mappedResponse)) return null;

  // SM-7: required computed_outputs source fields must be present and finite.
  const consumption = mappedResponse.consumption;
  if (!consumption || typeof consumption !== 'object' || Array.isArray(consumption)) return null;
  if (!finite(consumption.fabric_metres)) return null;
  if (!finite(consumption.fabric_consumption_mm)) return null;
  if (!finite(mappedResponse.lead_time_days)) return null;

  // SM-7: build the authoritative set of governed unit_sku handles from the
  // rule's pricing_components. A missing/empty/malformed pricing_components — or
  // one yielding no valid handles — fails closed.
  if (!Array.isArray(rule.pricing_components) || rule.pricing_components.length === 0) return null;
  const governedUnitSkus = new Set();
  for (const component of rule.pricing_components) {
    if (component && typeof component === 'object' && typeof component.unit_sku === 'string' && component.unit_sku) {
      governedUnitSkus.add(component.unit_sku);
    }
  }
  if (governedUnitSkus.size === 0) return null;

  // SM-7/SM-10: the fragment is emitted only for a response carrying valid,
  // governed, positive-integer quantities. Every quantities item must name a
  // governed handle (present in pricing_components) and a safe positive-integer
  // quantity. Malformed items, unknown handles, and zero/negative/fractional/
  // non-finite/unsafe quantities all fail closed. quantities are read as the
  // price-free "engine produced a valid result" signal but are NOT persisted.
  // (Slice-5 exact-coverage / duplicate-key checks are out of scope here.)
  const quantities = mappedResponse.quantities;
  if (!Array.isArray(quantities) || quantities.length === 0) return null;
  for (const q of quantities) {
    if (!q || typeof q !== 'object') return null;
    if (typeof q.unit_sku !== 'string' || !q.unit_sku) return null;
    if (!governedUnitSkus.has(q.unit_sku)) return null;
    if (!Number.isSafeInteger(q.quantity) || q.quantity <= 0) return null;
  }

  // SM-3: price_basis is not part of the engine-owned fragment; it is derived by
  // the order-write layer from verified Shopify order lines (ADR-004 PR-1).
  // Fragment validity does not depend on any evaluator price data.

  // SM-5: pristine raw_inputs — copy verbatim but never persist a reserved `_mm`
  // normalisation key (so the Target-1 normalised union cannot leak in).
  const raw_inputs = {};
  for (const key of Object.keys(rawInputs)) {
    if (key.endsWith('_mm')) continue;
    raw_inputs[key] = rawInputs[key];
  }

  // SM-1/SM-6: rename fabric_metres → fabric_consumption; fabric_consumption_mm is
  // taken from the engine verbatim, never derived. SM-2: no waste_pct_applied.
  // SM-8: engine fragment only (no references, bundle_ref, or order context).
  return {
    rule_key: rule.key,
    rule_version: rule.version,
    unit_of_measure: rule.unit_of_measure,
    raw_inputs,
    computed_outputs: {
      fabric_consumption: consumption.fabric_metres,
      fabric_consumption_mm: consumption.fabric_consumption_mm,
      lead_time_days: mappedResponse.lead_time_days,
    },
  };
}

const ConfigurationEngineAdapter = {
  /**
   * Inject the evaluator endpoint without changing evaluate()'s locked
   * three-argument signature (TT-1/TT-3). Invalid input clears any prior value
   * so a stale endpoint can never remain active.
   *
   * @param {{ endpoint?: unknown }} configuration
   * @returns {boolean} true only when a valid endpoint is active
   */
  configure(configuration = {}) {
    engineEndpoint = normaliseEngineEndpoint(configuration.endpoint);
    return engineEndpoint !== null;
  },

  /**
   * Evaluate a configuration_rule against customer inputs.
   * Returns quantities of unit SKUs, fabric consumption, and lead time. Price is
   * not returned — it is Shopify-owned and derived at order-write (ADR-004 PR-1).
   *
   * Targets 1–4: validate (T2) → normalise to mm (T1) → buildEngineRequest (T3)
   * → POST to the App Proxy engine endpoint → mapEngineResponse (T4). Returns a
   * Promise (async at T4 per §3.11). Fail-closed to emptyResult() on invalid
   * input, an unbuildable request, or ANY transport error. The output contract
   * is unchanged; the engine owns all calculation (Rule 3 / Rule 11).
   *
   * @param {Object} configurationRule - full configuration_rule metaobject entry
   * @param {Object} customerInputs    - raw customer dimension/choice inputs
   * @param {string} market            - active Shopify market handle
   * @returns {Promise<Object>} result
   */
  async evaluate(configurationRule, customerInputs, market) {
    // Target 2: validate RAW inputs BEFORE normalisation. On failure, fail closed
    // — return the empty result shape without normalising, without calling the
    // engine, and without inventing any price (Constitution Rule 11).
    const validation = validateInputs(customerInputs, configurationRule);
    if (!validation.valid) {
      if (typeof window !== 'undefined' && window.Shopify?.designMode) {
        console.log('[KF Engine] evaluate() — validation failed:', validation.errors);
      }
      return emptyResult();
    }

    // Target 1: unit normalisation happens here before the engine is called (§4.4).
    const normalisedInputs = this._normaliseInputs(customerInputs, configurationRule);

    // Target 3: build the RA-3 request payload (pure). Fail closed if it cannot
    // be built (malformed rule / non-object inputs / non-string market).
    const request = buildEngineRequest(configurationRule, normalisedInputs, market);
    if (request === null) {
      return emptyResult();
    }

    // Browser-only diagnostic; guarded so the module is importable in Node.
    if (typeof window !== 'undefined' && window.Shopify?.designMode) {
      console.log('[KF Engine] evaluate() — rule:', configurationRule?.key, 'inputs:', normalisedInputs);
    }

    // Step 6: live transport through the endpoint injected from the governed
    // theme setting. Shopify signs App Proxy requests; the Worker verifies the
    // signature. The browser stores no secret and computes no HMAC.
    // Fail closed on ANY transport error — network failure, non-200, invalid JSON,
    // timeout, or malformed body (the last handled by mapEngineResponse →
    // emptyResult()). The engine returns no price — pricing is Shopify-owned and
    // derived at order-write (ADR-004 PR-1).
    const endpoint = engineEndpoint;
    if (!endpoint || typeof AbortController !== 'function') {
      return emptyResult();
    }

    const controller = new AbortController();
    let timeoutId;
    try {
      timeoutId = setTimeout(() => controller.abort(), ENGINE_TIMEOUT_MS);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal: controller.signal,
      });
      if (!response || response.status !== 200) {
        return emptyResult();
      }
      let payload;
      try {
        payload = await response.json();
      } catch (_jsonError) {
        return emptyResult();
      }
      // RM-1/2/3: maps 1:1; any malformed field → emptyResult() (Rule 11).
      return mapEngineResponse(payload);
    } catch (_transportError) {
      return emptyResult();
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }
  },

  /**
   * Internal delegate to the pure `normaliseInputs` function. Retained for the
   * existing internal call site; carries no behaviour of its own.
   *
   * @param {Object} inputs
   * @param {Object} rule
   * @returns {Object}
   */
  _normaliseInputs(inputs, rule) {
    return normaliseInputs(inputs, rule);
  },
};

// Browser global — guarded so importing this module under Node (tests) does
// not throw a ReferenceError. Preserves existing storefront behaviour.
if (typeof window !== 'undefined') {
  window.ConfigurationEngineAdapter = ConfigurationEngineAdapter;
}

export default ConfigurationEngineAdapter;
export {
  ConfigurationEngineAdapter,
  normaliseInputs,
  toMillimetres,
  TO_MM,
  validateInputs,
  buildEngineRequest,
  mapEngineResponse,
  buildConfigSnapshotFragment,
  normaliseEngineEndpoint,
  ENGINE_TIMEOUT_MS,
};
